import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import {
    Upload,
    FileText,
    Send,
    Trash2,
    Loader2,
    CheckCircle2,
    FileCode,
    X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const DocumentAnalyzer = () => {
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isProcessed, setIsProcessed] = useState(false);
    const [question, setQuestion] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [isAsking, setIsAsking] = useState(false);
    const chatEndRef = useRef(null);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [chatHistory]);

    const onDrop = (acceptedFiles) => {
        const pdfFiles = acceptedFiles.filter(file => file.type === 'application/pdf');
        if (pdfFiles.length !== acceptedFiles.length) {
            toast.error('Only PDF files are allowed');
        }
        setFiles(prev => [...prev, ...pdfFiles]);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] }
    });

    const removeFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
        if (files.length === 1) setIsProcessed(false);
    };

    const formatError = (error) => {
        console.error('--- DETAILED ERROR LOG ---');
        console.dir(error);
        if (error.response) {
            console.error('Response Data:', error.response.data);
            console.error('Response Status:', error.response.status);
            console.error('Response Headers:', error.response.headers);

            const detail = error.response.data.detail;
            if (typeof detail === 'string') return detail;
            if (Array.isArray(detail)) {
                return detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
            }
            if (typeof detail === 'object' && detail !== null) {
                return JSON.stringify(detail);
            }
        } else if (error.request) {
            console.error('No response received:', error.request);
            return 'No response from server. Please check if backend is running.';
        } else {
            console.error('Error Message:', error.message);
        }
        return error.message || 'An unexpected error occurred';
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            toast.error('Please select at least one PDF');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });

        try {
            const response = await axios.post(`${BACKEND_URL}/api/documents/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });

            setIsProcessed(true);
            toast.success(response.data.message);
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(formatError(error));
        } finally {
            setIsUploading(false);
        }
    };

    const handleAsk = async (e) => {
        e.preventDefault();
        if (!question.trim()) return;
        if (!isProcessed) {
            toast.error('Please upload and process documents first');
            return;
        }

        const currentQuestion = question;
        setQuestion('');
        setChatHistory(prev => [...prev, { role: 'user', content: currentQuestion }]);
        setIsAsking(true);

        console.log('DEBUG: Sending question:', currentQuestion);
        try {
            const response = await axios.post(`${BACKEND_URL}/api/documents/ask`,
                { question: currentQuestion },
                { headers: { 'Content-Type': 'application/json' } }
            );
            console.log('DEBUG: Received response:', response.data);

            if (response.data.answer) {
                setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.answer }]);
            } else {
                throw new Error('No answer received from AI');
            }
        } catch (error) {
            console.error('Ask error:', error);
            const errMsg = formatError(error);
            toast.error(errMsg);
            setChatHistory(prev => [...prev, {
                role: 'assistant',
                content: `Error: ${errMsg}`,
                isError: true
            }]);
        } finally {
            setIsAsking(false);
        }
    };

    const clearChat = () => {
        setChatHistory([]);
    };

    return (
        <div className="container mx-auto p-6 space-y-6">
            <div className="flex flex-col space-y-2">
                <h1 className="text-3xl font-bold tracking-tight">AI Document Analyzer</h1>
                <p className="text-muted-foreground">Upload PDFs and ask questions about their content.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 min-h-0">
                {/* LEFT PANEL: Upload */}
                <div className="md:col-span-1 space-y-4 flex flex-col">
                    <Card className="flex-1 flex flex-col">
                        <CardHeader>
                            <CardTitle>Documents</CardTitle>
                            <CardDescription>Upload one or multiple PDFs to analyze.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 flex-1 flex flex-col overflow-hidden">
                            <div
                                {...getRootProps()}
                                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <input {...getInputProps()} />
                                <Upload className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                                <p className="text-sm text-gray-600">
                                    {isDragActive ? "Drop the PDFs here" : "Drag & drop PDFs, or click to select"}
                                </p>
                            </div>

                            {files.length > 0 && (
                                <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                                    <p className="text-xs font-semibold text-gray-500 uppercase">Selected Files ({files.length})</p>
                                    {files.map((file, index) => (
                                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md border border-gray-100 group">
                                            <div className="flex items-center space-x-2 truncate">
                                                <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                                <span className="text-sm truncate">{file.name}</span>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => removeFile(index)}
                                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X className="h-3 w-3 text-red-500" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {isUploading && (
                                <div className="space-y-2">
                                    <div className="flex justify-between text-xs">
                                        <span>Processing documents...</span>
                                        <span>{uploadProgress}%</span>
                                    </div>
                                    <Progress value={uploadProgress} className="h-2" />
                                </div>
                            )}

                            <Button
                                onClick={handleUpload}
                                className="w-full"
                                disabled={files.length === 0 || isUploading}
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="mr-2 h-4 w-4" />
                                        Upload & Process
                                    </>
                                )}
                            </Button>

                            {isProcessed && !isUploading && (
                                <p className="text-xs text-center text-green-600 font-medium">
                                    ✓ Ready for analysis
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* RIGHT PANEL: Chat */}
                <div className="md:col-span-2 flex flex-col h-[600px]">
                    <Card className="h-full flex flex-col overflow-hidden">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle>AI Assistant</CardTitle>
                                <CardDescription>Ask anything about your documents.</CardDescription>
                            </div>
                            <Button variant="ghost" size="sm" onClick={clearChat} disabled={chatHistory.length === 0}>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Clear Chat
                            </Button>
                        </CardHeader>
                        <CardContent className="flex-1 flex flex-col overflow-hidden p-0">
                            {/* Chat Window */}
                            <div
                                className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0"
                            >
                                {chatHistory.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-2">
                                        <FileCode className="h-12 w-12 opacity-20" />
                                        <p>No messages yet. Process documents and start asking questions.</p>
                                    </div>
                                ) : (
                                    chatHistory.map((msg, index) => (
                                        <div
                                            key={index}
                                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div
                                                className={`max-w-[80%] p-3 rounded-lg text-sm ${msg.role === 'user'
                                                    ? 'bg-blue-600 text-white rounded-br-none'
                                                    : msg.isError
                                                        ? 'bg-red-50 text-red-700 border border-red-100 rounded-bl-none'
                                                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                                                    }`}
                                            >
                                                <div className="prose prose-sm max-w-none prose-slate">
                                                    {msg.role === 'assistant' ? (
                                                        <ReactMarkdown 
                                                            remarkPlugins={[remarkGfm]}
                                                            components={{
                                                                table: ({node, ...props}) => (
                                                                    <div className="overflow-x-auto my-4 rounded-xl border border-gray-200 shadow-sm">
                                                                        <table className="min-w-full divide-y divide-gray-200" {...props} />
                                                                    </div>
                                                                ),
                                                                thead: ({node, ...props}) => <thead className="bg-gray-50/80" {...props} />,
                                                                th: ({node, ...props}) => <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider border-b border-gray-100" {...props} />,
                                                                td: ({node, ...props}) => <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-50 last:border-b-0" {...props} />,
                                                                tr: ({node, ...props}) => <tr className="even:bg-gray-50/30 hover:bg-blue-50/40 transition-colors" {...props} />,
                                                            }}
                                                        >
                                                            {msg.content}
                                                        </ReactMarkdown>
                                                    ) : (
                                                        <div className="whitespace-pre-wrap leading-relaxed">
                                                            {msg.content}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                                {isAsking && (
                                    <div className="flex justify-start">
                                        <div className="bg-gray-100 p-3 rounded-lg rounded-bl-none flex items-center space-x-2">
                                            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                                            <span className="text-sm text-gray-500 italic">Thinking...</span>
                                        </div>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>

                            {/* Input Area */}
                            <div className="p-4 border-t bg-gray-50">
                                <form onSubmit={handleAsk} className="flex space-x-2">
                                    <Input
                                        placeholder={isProcessed ? "Ask a question..." : "Process documents first..."}
                                        value={question}
                                        onChange={(e) => setQuestion(e.target.value)}
                                        disabled={!isProcessed || isAsking}
                                        className="bg-white"
                                    />
                                    <Button type="submit" disabled={!isProcessed || isAsking || !question.trim()}>
                                        {isAsking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                    </Button>
                                </form>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default DocumentAnalyzer;
